/**
 * src/types/core/interfaces.shared.ts
 * ─────────────────────────────────────────────────────────────────────────────
 * All shared interface definitions — typography, text elements, content config,
 * axes, layout, legend, data labels, animation, background, export, and the
 * ChartDataPoint base.
 *
 * Nothing in this file imports from chart modules or the registry.
 *
 * Chart2Video | CORSphere Software Inc.
 */

import type {
  FontFamily, FontWeight, FontStyle, HexColor, LabelDensity, GridStyle,
  LegendPosition, LineLegendFormat, DataLabelFormat, PointType,
  BackgroundType, RecordingQuality, ExportFormat, AnnotationType,
  AnnotationBinding, AnnotationTiming, LineDash, ArrowHeadStyle,
  PositionElementType, SelectedElementType,
} from './primitives';


// =============================================================================
// TYPOGRAPHY
// =============================================================================

/**
 * Universal font specification used by every text-rendering element.
 * All font sizes are actual canvas pixels, NOT multipliers.
 */
export interface TextStyle {
  fontFamily: FontFamily;
  fontSize:   number;
  fontWeight: FontWeight;
  fontStyle:  FontStyle;
  color:      HexColor;
  opacity:    number;       // 0–1. Default: 1.
  bgColor?:   string;       // highlight box fill colour. No box drawn when absent.
  bgSize?:    number;       // uniform box expansion in canvas px (default 6). Requires bgColor.
}

/** Semantic text roles with default styles. */
export type TextRole = 'title' | 'subtitle' | 'infoBox' | 'axisLabel' | 'dataLabel' | 'legend' | 'cellValue' | 'nodeLabel' | 'annotation';

/** Default TextStyle per semantic role. */
export const TEXT_STYLE_DEFAULTS: Record<TextRole, TextStyle> = {
  title:     { fontFamily: 'Arial', fontSize: 60, fontWeight: 'bold',   fontStyle: 'normal', color: '#0F172A', opacity: 1 },
  subtitle:  { fontFamily: 'Arial', fontSize: 28, fontWeight: 'normal', fontStyle: 'normal', color: '#475569', opacity: 1 },
  infoBox:   { fontFamily: 'Arial', fontSize: 18, fontWeight: 'normal', fontStyle: 'normal', color: '#FFFFFF', opacity: 1 },
  axisLabel: { fontFamily: 'Arial', fontSize: 20, fontWeight: '600',    fontStyle: 'normal', color: '#334155', opacity: 1 },
  dataLabel: { fontFamily: 'Arial', fontSize: 24, fontWeight: 'bold',   fontStyle: 'normal', color: '#1E293B', opacity: 1 },
  legend:    { fontFamily: 'Arial', fontSize: 20, fontWeight: 'normal', fontStyle: 'normal', color: '#1E293B', opacity: 1 },
  cellValue: { fontFamily: 'Arial', fontSize: 20, fontWeight: 'bold',   fontStyle: 'normal', color: '#FFFFFF', opacity: 1 },
  nodeLabel: { fontFamily: 'Arial', fontSize: 20, fontWeight: 'bold',   fontStyle: 'normal', color: '#FFFFFF', opacity: 1 },
  annotation:{ fontFamily: 'Arial', fontSize: 18, fontWeight: 'normal', fontStyle: 'normal', color: '#FFFFFF', opacity: 1 },
};


// =============================================================================
// TEXT ELEMENT
// =============================================================================

/**
 * A positioned, styled text block on the canvas.
 * Used for titles, subtitles, and info boxes.
 *
 * BASE ANCHORS (where position offset (0,0) lands):
 *   First title:    (960, 100)  — top-centre
 *   First subtitle: (960, 150)  — below title
 *   First infoBox:  (50, 200)   — top-left margin
 *   Additional elements of any type: (960, 540) — canvas centre
 */
export interface TextElement {
  id:       string;
  text:     string;
  style:    TextStyle;
  position: Position2D;
  visible:  boolean;
  animate?: boolean;   // Default: true.
}


// =============================================================================
// SPATIAL
// =============================================================================

/**
 * A 2D pixel offset from a base anchor in canvas coordinates.
 * (0, 0) = element is exactly at its base anchor position.
 * Safe ranges are format-dependent — use getSafeOffsetRange() from spatialUtils.
 */
export interface Position2D {
  x: number;
  y: number;
}

/**
 * A single stop in a colour gradient.
 * Stops must be in ascending order of `stop` (0.0 → 1.0).
 */
export interface GradientStop {
  /** 0.0 to 1.0. Always use isFinite() guard before addColorStop(). */
  stop:  number;
  color: HexColor;
}


// =============================================================================
// CONTENT CONFIG
// =============================================================================

export interface ContentConfig {
  titles:    TextElement[];
  subtitles: TextElement[];
  infoBoxes: TextElement[];
}


// =============================================================================
// AXIS CONFIG
// =============================================================================

export interface AxisConfig {
  visible:       boolean;
  labelStyle:    TextStyle;
  labelDensity:  LabelDensity;
  labelRotation: number;      // degrees. Range: -90 to 90. Default: 0.
  labelSpacing:  number;      // px gap between axis line and labels. Default: 30.
  showGridLines: boolean;
  gridStyle:     GridStyle;
  // Axis boundary line styling (the solid border at the plot area edge)
  lineVisible?:    boolean;   // show/hide boundary line. Default: true.
  lineColor?:      string;    // hex or rgba. Default: 'rgba(148,163,184,0.6)'.
  lineWidth?:      number;    // px 0.5–4. Default: 1.5.
  lineOpacity?:    number;    // 0–1. Default: 1.
  // Zero crossing line (only rendered when data spans positive + negative)
  zeroLineColor?:  string;    // Default: 'rgba(100,116,139,0.7)'.
  zeroLineWidth?:  number;    // px 0.5–4. Default: 1.5.
  zeroLineDash?:   'solid' | 'dashed' | 'dotted';  // Default: 'dashed'.
  // Tick marks (small perpendicular lines at each label position)
  tickLength?:     number;    // px 0–12. Default: 4. 0 = no tick marks.
  tickColor?:      string;    // Default: inherits lineColor.
  tickWidth?:      number;    // px 0.5–3. Default: 1.
  tickStyle?:      'solid' | 'dashed' | 'dotted';  // Default: 'solid'.
}

export interface YAxisConfig extends AxisConfig {
  /** 'zero' → force start at 0 | 'auto' → data min with padding | number → explicit */
  minValue: 'zero' | 'auto' | number;
  /** 'auto' → data max with padding | number → explicit */
  maxValue: 'auto' | number;
}

export interface AxesConfig {
  x: AxisConfig;
  y: YAxisConfig;
}


// =============================================================================
// CHART LAYOUT CONFIG
// =============================================================================

export interface ChartLayoutConfig {
  chartPosition: Position2D;
  chartMargin: {
    top:    number;   // Range: 50–400.  Default: 180.
    right:  number;   // Range: 20–300.  Default: 100.
    bottom: number;   // Range: 50–300.  Default: 150.
    left:   number;   // Range: 50–300.  Default: 150.
  };
}


// =============================================================================
// LEGEND CONFIG
// =============================================================================

/**
 * Legend configuration — controls DIFFERENT things per chart type:
 *
 * LINE:        Per-point pills. legend.visible shows/hides pills.
 *              legend.labelFormat controls pill text (name/value/name+value/xy).
 *              legend.pillBgColor, legend.showDot, legend.position control visual style.
 *              dataLabels config is NOT used by line chart renderer.
 *
 * BAR:         Unused for single-series bar. For bar-stacked: series color key.
 *
 * PIE:         NOT used. Radial labels are controlled by dataLabels.format.
 *              legend.visible has no effect on pie renderer.
 *
 * HEATMAP:     legend.visible gates the gradient scale bar.
 *              Scale bar labels use spacing.legendTitle/legendMinLabel/legendMidLabel/legendMaxLabel.
 *
 * SCATTER:     Group color key. Auto-detects groups from data[].group field.
 *              legend.visible controls whether group legend appears.
 *              legend.labelFormat controls legend entry text.
 *
 * BAR-STACKED: Series color key. legend.visible + legend.labelFormat control entries.
 */
export interface LegendConfig {
  visible:        boolean;
  style:          TextStyle;
  position:       LegendPosition;
  pillBgColor?:   string;
  pillStyle?:     'pill' | 'none' | 'colored-text';
  showDot?:       boolean;
  labelFormat?:   LineLegendFormat;
  endLabelCount?: number;
  bgColor?:       string;
}


// =============================================================================
// DATA LABEL CONFIG
// =============================================================================

/**
 * Data label configuration — controls per-element labels on chart:
 *
 * BAR:         Value label above/inside each bar. dataLabels.format = 'value' | 'name' | 'name+value'.
 *
 * LINE:        NOT used by line chart renderer. Per-point pills use LegendConfig instead.
 *
 * PIE:         Radial slice labels. dataLabels.format = 'percentage' | 'value' | 'name' | 'name+value' | 'name+percent'.
 *              Controls BOTH value/percentage AND slice name display.
 *
 * HEATMAP:     Cell value labels. dataLabels.format = 'value' | 'percentage'.
 *
 * SCATTER:     Point name/label. Controlled by spacing.pointLabelMode, not dataLabels.format.
 *
 * BAR-STACKED: Per-segment value labels + total labels above columns.
 */
export interface DataLabelConfig {
  visible:  boolean;
  style:    TextStyle;
  format:   DataLabelFormat;
  bgStyle?: 'none' | 'pill' | 'underline';
}


// =============================================================================
// DATA POINT STYLE OVERRIDE
// =============================================================================

/**
 * Optional style override for a single data point.
 * Any field left undefined falls back to the chart's global config.
 */
export interface DataPointStyle {
  // ── Fill & stroke ──────────────────────────────────────────────────────────
  fillColor?:       HexColor;
  fillOpacity?:     number;
  strokeColor?:     HexColor;
  strokeWidth?:     number;

  // ── Shape ──────────────────────────────────────────────────────────────────
  pointType?:       PointType;
  customImage?:     string;
  customText?:      string;
  iconType?:        string;

  // ── Emphasis ───────────────────────────────────────────────────────────────
  highlight?:       boolean;
  highlightColor?:  HexColor;
  glowSize?:        number;
  sizeMultiplier?:  number;

  // ── Shadow ─────────────────────────────────────────────────────────────────
  shadowColor?:     string;
  shadowBlur?:      number;
  shadowOffsetX?:   number;
  shadowOffsetY?:   number;

  // ── Image fill ─────────────────────────────────────────────────────────────
  imageUrl?:        string;
  imageOpacity?:    number;
  imageOffsetX?:    number;
  imageOffsetY?:    number;
  imageZoom?:       number;

  // ── Per-point label nudge ─────────────────────────────────────────────────
  labelOffsetX?:    number;
  labelOffsetY?:    number;

  // ── Animation override ─────────────────────────────────────────────────────
  animationDelay?:  number;
  animationStyle?:  string;
  physicsOverride?: {
    /** 0 = no bounce, 1 = max bounce. Same semantics as AnimationPhysics.bounce. */
    bounce?:        number;
    springFreq?:    number;
    staggerSpread?: number;
  };

  // ── Visibility ────────────────────────────────────────────────────────────
  hidden?:          boolean;
  annotationIds?:   string[];

  // ── Per-point label ────────────────────────────────────────────────────────
  labelText?:       string;
  labelPosition?:   'above' | 'below' | 'left' | 'right' | 'inside-top' | 'inside-bottom' | 'center';
  labelBgStyle?:    'none' | 'pill' | 'underline';

  // ── Line chart pill ────────────────────────────────────────────────────────
  pillStyle?:       'pill' | 'none' | 'colored-text';
  pillHidden?:      boolean;
  pillText?:        string;
  pillBgColor?:     string;
  pillFontColor?:   string;
  pillFontSize?:    number;
  pillFontWeight?:  string;

  // ── Font shortcuts ─────────────────────────────────────────────────────────
  fontSize?:        number;
  fontColor?:       HexColor;
  fontWeight?:      FontWeight;

  // ── Full override ──────────────────────────────────────────────────────────
  labelOverride?:   Partial<TextStyle>;

  // ── Pie slice name label ───────────────────────────────────────────────────
  nameText?:        string;
  nameOverride?:    { fontSize?: number; color?: string; fontWeight?: string; };
  labelLineGap?:    number;

  // ── Bar/Line x-axis label ─────────────────────────────────────────────────
  xLabelOverride?:  Partial<TextStyle>;
}


// =============================================================================
// ANIMATION CONFIG
// =============================================================================

/**
 * Physics parameters for animation.
 *   bounce        — 0 (no bounce, smooth settle) → 1 (maximum spring bounce). Default: 0.35.
 *                   Internally: damping = 1 - (bounce × 0.9), so bounce=0 → critical damping.
 *   springFreq    — oscillation frequency 0.5–6. Default: 2.5.
 *                   NOTE: this is NOT animation speed — use animation.duration for that.
 *   staggerSpread — 0 (simultaneous) → 1 (fully sequential). Default: 0.45.
 */
export interface AnimationPhysics {
  /**
   * Bounce intensity. 0 = no bounce (smooth, critically damped settle).
   * 1 = maximum spring bounce. Range: 0–1. Default: 0.35.
   * Maps to damping ratio as: damping = 1 - (bounce × 0.9).
   * Left (0) = smooth settle. Right (1) = energetic spring.
   */
  bounce?:        number;
  /** Spring oscillation frequency. Range: 0.5–6. Default: 2.5.
   *  Higher = faster oscillations. This is NOT animation speed — use animation.duration. */
  springFreq?:    number;
  /** 0 = all elements start simultaneously. 1 = fully sequential. Default: 0.45. */
  staggerSpread?: number;
}

/**
 * Per-chart phase window overrides for the choreography system.
 * Each entry is [start, end] as a fraction of raw 0→1 animation time.
 * Unset fields use defaults from CHOREOGRAPHY_DEFAULTS[chartType] in chartMath.ts.
 * Agents: set these only when the default staging feels wrong for specific content.
 */
export interface ChoreographyConfig {
  axes?:        [number, number];
  data?:        [number, number];
  labels?:      [number, number];
  legend?:      [number, number];
  annotations?: [number, number];
}

export interface AnimationConfig {
  duration:      number;           // total animation seconds. Range: 0.5–20.
  style:         string;           // must be in VALID_ANIMATION_STYLES[chartType]
  textSpeed:     number;           // text animation speed multiplier. Range: 0.1–3. Default: 1.
  physics?:      AnimationPhysics;
  choreography?: ChoreographyConfig; // phase window overrides. Uses CHOREOGRAPHY_DEFAULTS when absent.
}

/**
 * Structured definition for a single animation style.
 * Single source of truth for documentation, agent prompts, and UI dropdowns.
 */
export interface AnimationStyleDef {
  value:           string;
  label:           string;
  charts:          string[];
  physicsCategory: 'smooth' | 'spring' | 'expressive' | 'staggered' | 'wave' | 'physics';
  description:     string;
  loopFriendly:    boolean;
}


// =============================================================================
// BACKGROUND CONFIG
// =============================================================================

export interface BackgroundConfig {
  type:           BackgroundType;
  color:          HexColor;
  image:          string | null;
  opacity:        number;
  contrast:       number;
  gradientEnd?:   HexColor;
  gradientAngle?: number;
}


// =============================================================================
// EXPORT CONFIG
// =============================================================================

export interface ExportConfig {
  quality:      RecordingQuality;
  isLooping:    boolean;
  canvasFormat: ExportFormat;
}


// =============================================================================
// CHART DATA POINT — BASE
// =============================================================================

/**
 * Base fields shared by all data point types.
 * Never instantiate directly — use a chart-specific subtype.
 */
export interface ChartDataPoint {
  id:             string;
  name:           string;
  color:          HexColor;
  order?:         number;
  visible?:       boolean;
  styleOverride?: DataPointStyle;
  pointType?:     PointType;
  customImage?:   string;
  visualElements?: Record<string, unknown>[];
}

/**
 * Describes the clickable canvas area for a single data point.
 * Computed by each renderer and stored in a ref for hit testing.
 */
export interface PointHitRegion {
  id:          string;
  index:       number;
  type:        'bar' | 'point' | 'slice' | 'cell';
  x:           number;
  y:           number;
  radius?:     number;
  w?:          number;
  h?:          number;
  innerR?:     number;
  outerR?:     number;
  startAngle?: number;
  endAngle?:   number;
}


// =============================================================================
// SELECTED ELEMENT — context-sensitive panel system
// =============================================================================

/**
 * Passed from each chart page into ChartStudioLayout to trigger element-panel mode.
 */
export interface SelectedElement {
  type:          SelectedElementType;
  index:         number;
  id:            string;
  label:         string;
  pointData?:    any;  // union of TextElement | AnyDataPoint at runtime — typed by element.type discriminant
  annotationId?: string;
}

/**
 * A single element entry for the CanvasPositionOverlay component.
 */
export interface PositionItem {
  label:             string;
  position:          Position2D;
  setter:            (pos: Position2D) => void;
  elementType:       PositionElementType;
  elementIndex:      number;
  fontSize?:         number;
  onFontSizeChange?: (newSize: number) => void;
}


// =============================================================================
// CHART IMPERATIVE HANDLE — shared ref type for all chart renderers
// =============================================================================

/**
 * Methods and properties exposed by every chart renderer via useImperativeHandle.
 * Used by ChartRecorder, chart pages, and any parent that holds a chart ref.
 */
export interface ChartImperativeHandle {
  resetAnimation:       () => void;
  setAnimationProgress: (p: number) => void;
  resetAndShowFinal:    () => void;
  renderFrame:          (progress: number) => void;
  getTotalValue?:       () => number;
  animationDuration:    number;
  getAnimationProgress: () => number;
  playAnimation:        () => void;
  preloadImages:        () => void;
  hasData:              () => boolean;
  hasFallbackData:      () => boolean;
  getPointRegions:      () => PointHitRegion[];
  // Sprint B — Data Stream / Timeline Mode
  // Implemented by streaming chart types. Optional so non-streaming charts
  // don't need to change.
  setStreamData?:    (data: unknown[], config: DataStreamConfig) => void;
  clearStream?:      () => void;
  getStreamDuration?: () => number;
  /** Number of discrete keyframes / windows / points in the streamed dataset. */
  getFrameCount?:    () => number;
  /** Human-readable label per frame — shown in TimelineControls scrubber. */
  getFrameLabels?:   () => string[];
  /**
   * Clear stored render state (position cache for state-carrying interpolation).
   * Called by useStreamPlayback on reset() and seekTo() to prevent bars from
   * animating from stale positions after a seek.
   * Only implemented by BarRaceChart — optional on all others.
   */
  clearRenderedState?: () => void;
}

// =============================================================================
// DATA STREAM CONFIG — Sprint B
// Used by bar-race now. Line rolling-window and scatter growing-trace: future.
// =============================================================================

export type DataStreamModeType =
  | 'frames'          // bar-race: discrete keyframes interpolated between
  | 'growing-trace'   // future: reveal data points progressively
  | 'rolling-window'; // future: sliding window of N visible points

export interface DataStreamConfig {
  mode:             DataStreamModeType;
  /** frames: total keyframe count. rolling-window: window size. growing-trace: total points. */
  windowSize:       number;
  /** Frames/points per second of wall-clock time. Default 2 for frames, 60 for others. */
  pointsPerSecond:  number;
  totalPoints?:     number;
  durationSeconds?: number;
}
